import React, { useState } from 'react';
import { BarList, Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, useAdminGet } from '../kit';
import { rowsOf, str } from '../ipintel/shared';
import PageDrawer, { type PageRef } from './PageDrawer';
import {
  CTA_LABELS, DailyBars, OpenLink, type Range, S, SOURCE_LABELS, STATE_LABELS, breakdown, eventLabel,
  fmtInt, nameOf, rangeParams,
} from './shared';

/**
 * Overview — GET tech_pages/overview {from, to, exclude_internal}: the
 * headline numbers for every public tech / university page, views by day and
 * by visitor state, the top pages, where visitors came from and which CTAs
 * they used. A top page opens the same drawer as the page tables.
 */

const TOP_COLS = ['title', 'kind', 'views', 'visitors', 'path'];
const TOP_LABELS: Record<string, string> = { title: 'Page', kind: 'Kind', views: 'Views', visitors: 'Visitors', path: 'URL' };

const Overview: React.FC<{ range: Range; onOpenTab: (tab: string) => void }> = ({ range, onOpenTab }) => {
  const rp = rangeParams(range);
  const key = JSON.stringify(rp);
  const [tick, setTick] = useState(0);
  const res = useAdminGet(S, 'overview', rp, [key, tick]);
  const [open, setOpen] = useState<PageRef | null>(null);
  const d = res.data || {};
  const k = d.kpis || {};
  const byDay = rowsOf(d.by_day, 'days');
  const byState = breakdown(d.by_state, x => nameOf(x, STATE_LABELS));
  const referrers = breakdown(d.top_referrers, x => nameOf(x, SOURCE_LABELS));
  const ctas = breakdown(d.top_ctas, x => (CTA_LABELS[x] || eventLabel(x)));
  const top = rowsOf(d.top_pages);

  return (
    <div className="flex flex-col gap-4">
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !res.data ? <Loading label="Counting page traffic…" /> : (
        <>
          <KPIRow>
            <KPI label="Tech pages" value={fmtInt(k.tech_pages)} hint="live URLs · click to list"
              onClick={() => onOpenTab('Tech pages')} />
            <KPI label="University pages" value={fmtInt(k.university_pages)} hint="live URLs · click to list"
              onClick={() => onOpenTab('University pages')} />
            <KPI label="Page views" value={fmtInt(k.views)} hint="people, not crawlers" />
            <KPI label="Unique visitors" value={fmtInt(k.unique_visitors)} />
            <KPI label="Sign-ups from pages" value={fmtInt(k.signups_from_pages)} hint="signed up on a tech / university page" />
            <KPI label="CTA clicks" value={fmtInt(k.cta_clicks)} hint="contact, IP assessment, share, bookmark" />
            <KPI label="Locked-section clicks" value={fmtInt(k.locked_clicks)} hint="sign in / upgrade prompts" />
            <KPI label="Crawler hits" value={fmtInt(k.crawler_hits)} hint="Google, Bing & co (server-side)" />
          </KPIRow>

          <Section title="Views by day" right={<Btn small onClick={() => setTick(t => t + 1)} disabled={res.loading}>Refresh</Btn>}>
            <DailyBars days={byDay} series={[{ key: 'views', label: 'Views' }, { key: 'visitors', label: 'Visitors' }]} />
          </Section>

          <div className="grid md:grid-cols-3 gap-4">
            <Section title="Views by visitor state">
              {byState.length ? <BarList items={byState} /> : <div className="text-sm text-grayscale-1100">No views yet.</div>}
              <div className="text-[11px] text-grayscale-1100">
                Pre-login = not signed in; post-login = signed in without a paid plan; paid = trial or Voyager.
              </div>
            </Section>
            <Section title="Top referrers">
              {referrers.length ? <BarList items={referrers.slice(0, 10)} /> : <div className="text-sm text-grayscale-1100">No referrers yet.</div>}
            </Section>
            <Section title="Top CTAs">
              {ctas.length ? <BarList items={ctas.slice(0, 10)} /> : <div className="text-sm text-grayscale-1100">No CTA clicks yet.</div>}
            </Section>
          </div>

          <Section title="Top pages">
            <DataTable rows={top} columns={TOP_COLS} labels={TOP_LABELS} maxHeight="30rem"
              empty="No page views in this period."
              render={{
                title: r => (
                  <button type="button" title="Open this page's detail"
                    onClick={() => setOpen({ kind: str(r.kind) || 'tech', target: str(r.target), path: str(r.path), title: str(r.title), row: r })}
                    className="text-left font-medium text-grayscale-1300 underline underline-offset-2 block max-w-[24rem] truncate">
                    {str(r.title) || str(r.target)}
                  </button>
                ),
                kind: r => (str(r.kind) === 'university' ? 'University' : 'Tech'),
                views: r => <span className="tabular-nums font-medium">{fmtInt(r.views)}</span>,
                visitors: r => <span className="tabular-nums">{fmtInt(r.visitors)}</span>,
                path: r => (
                  <span className="inline-flex items-center gap-2">
                    <span className="font-mono text-[11px] text-grayscale-1100 max-w-[18rem] truncate" title={str(r.path)}>{str(r.path)}</span>
                    <OpenLink path={r.path} label="↗" />
                  </span>
                ),
              }} />
          </Section>
        </>
      )}
      {open && <PageDrawer page={open} range={range} onClose={() => setOpen(null)} />}
    </div>
  );
};

export default Overview;
